import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import SafeIcon from '../common/SafeIcon';
import { FiUsers, FiCalendar, FiPhone, FiRefreshCw, FiTrash2, FiDownload, FiCheckCircle, FiXCircle, FiUserCheck } from 'react-icons/fi';
import { apiCall } from '../api/client';

const ParticipantsList = ({ token }) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchParticipants = async () => {
    setLoading(true);
    setError('');
    try {
      // تم إضافة limit=10000 لجلب كامل البيانات دفعة واحدة
      const response = await apiCall('/read/participants?limit=10000', 'GET', null, token);
      setParticipants(response.data || []);
    } catch (err) {
      setError(err.message || 'فشل في تحميل قائمة المشاركين');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParticipants();
  }, [token]);

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المشارك نهائياً؟')) return;
    setDeletingId(id);
    try {
      await apiCall(`/delete/participants/${id}`, 'DELETE', null, token);
      setParticipants(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      alert('حدث خطأ أثناء الحذف: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const toggleAttendance = async (participant) => {
    const newStatus = participant.attendance_status === 'حضر' ? 'لم يحضر' : 'حضر';
    setUpdatingId(participant.id);
    const originalParticipants = [...participants];
    
    setParticipants(prev => 
      prev.map(p => p.id === participant.id ? { ...p, attendance_status: newStatus } : p)
    );

    try {
      await apiCall(`/update/participants/${participant.id}`, 'PUT', { ...participant, attendance_status: newStatus }, token);
    } catch (err) {
      setParticipants(originalParticipants);
      alert('فشل تحديث حالة الحضور: ' + err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleExportToExcel = () => {
    if (participants.length === 0) {
      alert('لا توجد بيانات لتصديرها');
      return;
    }
    const dataToExport = participants.map((p, index) => ({
      'م': index + 1,
      'اسم المشارك': p.participant_name,
      'رقم الجوال': p.mobile_number,
      'تاريخ التسجيل': p.registration_date,
      'حالة الحضور': p.attendance_status || 'لم يحضر'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    worksheet['!dir'] = 'rtl';
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "المشاركين");
    
    const fileName = `قائمة_المشاركين_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }} 
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden"
    >
      <div className="p-6 border-b border-slate-50 flex flex-wrap justify-between items-center gap-4 bg-slate-50/50">
        <div className="flex items-center gap-3">
          <div className="bg-slate-800 text-white p-2.5 rounded-xl shadow-sm">
            <SafeIcon icon={FiUsers} className="text-xl" />
          </div>
          <div>
            <span className="font-bold text-slate-800 block text-lg">إحصائيات الحضور</span>
            <div className="flex gap-3 mt-0.5">
              <span className="text-xs text-slate-500 font-bold">المسجلين: {participants.length}</span>
              <span className="text-xs text-green-600 font-bold">
                الحاضرون: {participants.filter(p => p.attendance_status === 'حضر').length}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button 
            onClick={handleExportToExcel}
            disabled={loading || participants.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all disabled:opacity-50 font-bold text-sm shadow-sm active:scale-95"
          >
            <SafeIcon icon={FiDownload} />
            تصدير Excel
          </button>
          
          <button 
            onClick={fetchParticipants}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-all disabled:opacity-50 font-bold text-sm active:scale-95"
          >
            <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
            تحديث
          </button>
        </div>
      </div>

      <div className="p-6">
        {error ? (
          <div className="p-8 text-center text-red-500 bg-red-50 rounded-2xl border border-red-100">
            <p className="font-bold">{error}</p>
          </div>
        ) : loading && participants.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center text-slate-400">
            <SafeIcon icon={FiRefreshCw} className="animate-spin text-4xl mb-4" />
            <p className="font-bold">جاري تحميل البيانات...</p>
          </div>
        ) : participants.length === 0 ? (
          <div className="py-20 text-center text-slate-400">
            <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiUsers} className="text-3xl" />
            </div>
            <p className="text-lg font-bold">لا يوجد مشاركين مسجلين حالياً</p>
          </div>
        ) : (
          <div className="grid gap-3">
            <AnimatePresence mode="popLayout">
              {participants.map((participant, index) => {
                const isPresent = participant.attendance_status === 'حضر';
                return (
                  <motion.div
                    key={participant.id || index}
                    layout
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: index * 0.02 }}
                    className={`flex flex-col sm:flex-row justify-between items-start sm:items-center p-5 border rounded-2xl transition-all gap-4 group ${
                      isPresent 
                        ? 'border-green-100 bg-green-50/40 shadow-sm' 
                        : 'border-slate-50 hover:border-slate-200 hover:bg-slate-50/50'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-lg text-slate-800">{participant.participant_name}</h3>
                        {isPresent && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-600 text-white text-[10px] font-bold rounded-lg uppercase tracking-wider">
                            <SafeIcon icon={FiUserCheck} />
                            مؤكد
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 font-medium">
                        <span className="flex items-center gap-1.5" dir="ltr">
                          <SafeIcon icon={FiPhone} className="text-slate-400" />
                          {participant.mobile_number}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-slate-200 hidden sm:block"></span>
                        <span className="flex items-center gap-1.5">
                          <SafeIcon icon={FiCalendar} className="text-slate-400" />
                          {participant.registration_date}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 self-end sm:self-center">
                      <button
                        onClick={() => toggleAttendance(participant)}
                        disabled={updatingId === participant.id}
                        className={`min-w-[120px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all shadow-sm active:scale-95 border ${
                          isPresent
                            ? 'bg-green-600 border-green-600 text-white hover:bg-green-700'
                            : 'bg-white border-red-200 text-red-600 hover:bg-red-50'
                        }`}
                      >
                        {updatingId === participant.id ? (
                          <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                        ) : isPresent ? (
                          <>
                            <SafeIcon icon={FiCheckCircle} />
                            حضر
                          </>
                        ) : (
                          <>
                            <SafeIcon icon={FiXCircle} />
                            لم يحضر
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => handleDelete(participant.id)}
                        disabled={deletingId === participant.id}
                        className="p-2.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all sm:opacity-0 sm:group-hover:opacity-100 disabled:opacity-50"
                        title="حذف"
                      >
                        <SafeIcon 
                          icon={deletingId === participant.id ? FiRefreshCw : FiTrash2} 
                          className={deletingId === participant.id ? 'animate-spin' : ''} 
                        />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ParticipantsList;